import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import VisibilitySensor from 'react-visibility-sensor';
import isEqual from 'lodash/isEqual';

import useApi from 'hooks/useApi';
import useUpdateDateSettings from 'hooks/useUpdateDateSettings';
import useUpdateMainSettings from 'hooks/useUpdateMainSettings';
import useUpdateMoreOptions from 'hooks/useUpdateMoreOptions';
import usePrevious from 'hooks/usePrevious';

import BaseWidget from 'components/Widget/BaseWidget';
import selectComponent from './utils/componentParams';

const WidgetWrapper = ({
  widget,
  dashboardData,
  updateWidget,
  dispatchCannedDashboardList,
  isEditMode,
  globalPickerDateState
}) => {
  const [tableExportData, setTableExportState] = useState();
  const [isWidgetVisible, setIsWidgetVisible] = useState(false);
  const [filters, setFilters] = useState();
  const componentRef = useRef(null);
  const prevWidget = usePrevious(widget);
  const [state, makeRequest] = useApi();
  const { data } = state;

  const [onDateSettingsSubmit] = useUpdateDateSettings({
    widget,
    updateWidget,
    dashboardData,
    dispatchCannedDashboardList
  });
  const [onMainSettingsSubmit] = useUpdateMainSettings({
    widget,
    updateWidget,
    dashboardData,
    dispatchCannedDashboardList
  });
  const [onMoreOptionsSubmit] = useUpdateMoreOptions({
    widget,
    updateWidget,
    dashboardData,
    dispatchCannedDashboardList
  });

  const { id, overlay, masterObjectId } = widget && widget;
  const { publicId } = overlay && overlay;
  const widgetType = overlay && overlay.baseWidget.literal;

  const {
    component: Component,
    dataSourceHandler,
    endpoint,
    query,
    shouldDisplayDates
  } = useMemo(() => selectComponent(publicId), [publicId]);

  const dataSource = useMemo(() => {
    if (data && dataSourceHandler) return dataSourceHandler(data, widget);
    return null;
  }, [data, dataSourceHandler, widget]);

  const setTableExportData = useCallback(({ title, columns, rows }) => {
    setTableExportState({ title, columns, rows });
  }, []);

  const { paramDefaultValue, paramName } = query || {};
  const [queryParam, setQueryParam] = useState({ [paramName]: paramDefaultValue });

  const setChildParam = useCallback(
    param => {
      setQueryParam({ [paramName]: param });
    },
    [paramName]
  );

  const handleVisibilityChange = isVisible => {
    if (!isWidgetVisible) setIsWidgetVisible(isVisible);
  };

  const updateWidgetDateState = data => {
    onDateSettingsSubmit(data).then(() => {
      fetchWidgetData();
    })
  }

  const fetchWidgetData = useCallback(() => {
    const reqParams = endpoint && endpoint({ id, masterObjectId, filters, ...queryParam });
    makeRequest(reqParams);
  }, [endpoint, id, makeRequest, masterObjectId, queryParam, filters]);

  useEffect(() => {
    if (isWidgetVisible || (prevWidget && !isEqual(prevWidget, widget))) {
      fetchWidgetData();
    }
  }, [fetchWidgetData, isWidgetVisible, widget]); // eslint-disable-line

  useEffect(() => {
    if (globalPickerDateState && Object.keys(globalPickerDateState).length > 0)
    {
      // Approach 3 with axios request directly
      updateWidgetDateState(globalPickerDateState);

      // Comments are left due to unknown reason why approach 1 and 2 not working - WIP

      // Approach 1 with data
      // const result = onDateSettingsSubmit(globalPickerDateState);
      // if (result) {
      //   fetchWidgetData()
      // }

      // Approach 2 with Promise
      // onDateSettingsSubmit(globalPickerDateState).then((response) => {
      //   fetchWidgetData();
      // });
    }
  }, [globalPickerDateState])

  return (
    <BaseWidget
      widgetRef={componentRef}
      widget={widget}
      onDateSettingsSubmit={updateWidgetDateState}
      onMoreOptionsSubmit={onMoreOptionsSubmit}
      onMainSettingsSubmit={onMainSettingsSubmit}
      dynamicTitle={data && data.widgetTitle}
      dashboardData={dashboardData}
      tableExportData={tableExportData}
      isEditMode={isEditMode}
      dispatchDashboard={updateWidget}
      shouldDisplayDates={shouldDisplayDates}
      state={state}
      setFilters={setFilters}
      globalPickerDateState={globalPickerDateState}
    >
      <VisibilitySensor onChange={handleVisibilityChange} partialVisibility>
        <Component
          forwardRef={componentRef}
          dataSource={dataSource}
          setQueryParam={setChildParam}
          queryParam={queryParam}
          widgetType={widgetType}
          state={state}
          widget={widget}
          dashboardData={dashboardData}
          dispatchCannedDashboardList={dispatchCannedDashboardList}
          setTableExportData={setTableExportData}
        />
      </VisibilitySensor>
    </BaseWidget>
  );
};

WidgetWrapper.propTypes = {
  isDashboardLoading: PropTypes.bool.isRequired,
  updateWidget: PropTypes.func.isRequired,
  dispatchCannedDashboardList: PropTypes.func.isRequired,
  dashboardData: PropTypes.shape({}).isRequired,
  widget: PropTypes.shape({
    title: PropTypes.string,
    colSpan: PropTypes.number.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    dateRangeType: PropTypes.string,
    dateMatchType: PropTypes.number.isRequired,
    customDateMatchType: PropTypes.number.isRequired,
    isChartTooltipDisplayed: PropTypes.bool.isRequired,
    isTitleDisplayed: PropTypes.bool.isRequired,
    isDateDisplayed: PropTypes.bool.isRequired,
    isLegendDisplayed: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    dateRangeTypeId: PropTypes.number.isRequired,
    masterObjectId: PropTypes.number.isRequired,
    masterObjectTypeId: PropTypes.number,
    overlay: PropTypes.shape({
      baseWidget: PropTypes.shape({
        name: PropTypes.string.isRequired,
        widgetType: PropTypes.shape({
          name: PropTypes.string.isRequired
        }).isRequired
      }).isRequired
    }).isRequired
  }).isRequired,
  isEditMode: PropTypes.bool
};

WidgetWrapper.defaultProps = {
  isEditMode: false
};

export default WidgetWrapper;
